import { CameraData, ModelViewer, useModelViewerLoader } from "@centricsoftware/model-viewer";
import IconButton from "designSystem/components/button/IconButton";
import { Box, Button, FormField, FormControlLabel, GridColumn, GridRow, ModalDialog, Text, TextField } from "designSystem/index";
import { ButtonEnum } from "designSystem/types/button-types";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import XAxis3DIcon from "../icon/toolbar/axisIcons/XAxis3DIcon";
import YAxis3DIcon from "../icon/toolbar/axisIcons/YAxis3DIcon";
import ZAxis3DIcon from "../icon/toolbar/axisIcons/ZAxis3DIcon";
import CameraPositionDialogStore, { CUBE_FACE_DATA, CameraPositionDialogStoreContext, useCameraPositionDialogStore } from "./CameraPositionStore";
import css from "./NewCameraPositionDialog.module.css";
import { REQUIRED_LABEL } from "../snapshotTemplates/snapshotUtils";

interface CameraPositionDialogProps {
	/** store created in action controller */
	store:CameraPositionDialogStore;
}

const CameraPositionFormData: FunctionComponent = observer(() => {
	const cameraDialogStore = useCameraPositionDialogStore();
	const { cameraPositionName, cameraPositionDescription, modelViewerCameraData, saveErrorMessage, setCameraPositionName, setCameraPositionDescription } = cameraDialogStore;

	return (
		<GridColumn gap={4}>
			<FormField
				control={(
					<TextField
						autoFocus
						error={!!saveErrorMessage}
						fullWidth
						onChange={setCameraPositionName}
						placeholderRes="3DCameraPositionName"
						value={cameraPositionName}
					/>
				)}
				fullWidth
				invalidMessage={saveErrorMessage}
				label={<Text bold res="3DCameraPositionName" />}
				labelPlacement="top"
				required
				sx={REQUIRED_LABEL}
			/>
			<FormControlLabel
				className={css.label}
				control={(
					<TextField
						onChange={setCameraPositionDescription}
						placeholderRes="Description"
						value={cameraPositionDescription}
					/>
				)}
				fullWidth
				labelPlacement="top"
				res="Description"
			/>
			<Text bold res="CameraSettings" />
			<GridRow
				grow
				growChild={0}
			>
				<Text className={css.axisLabel} res="Location" />
				<Box className={css.axisContainer}>
					<XAxis3DIcon />
					<Text
						className={css.axisTextField}
						text={modelViewerCameraData?.position[0]}
					/>
				</Box>
				<Box className={css.axisContainer}>
					<YAxis3DIcon />
					<Text
						className={css.axisTextField}
						text={modelViewerCameraData?.position[1]}
					/>
				</Box>
				<Box className={css.axisContainer}>
					<ZAxis3DIcon />
					<Text
						className={css.axisTextField}
						text={modelViewerCameraData?.position[2]}
					/>
				</Box>
			</GridRow>
			<GridRow
				grow
				growChild={0}
			>
				<Text className={css.axisLabel} res="Rotation" />
				<Box className={css.axisContainer}>
					<XAxis3DIcon />
					<Text
						className={css.axisTextField}
						text={modelViewerCameraData?.rotation[0]}
					/>
				</Box>
				<Box className={css.axisContainer}>
					<YAxis3DIcon />
					<Text
						className={css.axisTextField}
						text={modelViewerCameraData?.rotation[1]}
					/>
				</Box>
				<Box className={css.axisContainer}>
					<ZAxis3DIcon />
					<Text
						className={css.axisTextField}
						text={modelViewerCameraData?.rotation[2]}
					/>
				</Box>
			</GridRow>
		</GridColumn>
	);
});

const CubeFaceMenu: FunctionComponent = observer(() => {
	const cameraDialogStore = useCameraPositionDialogStore();
	const { modelViewerSetCameraData } = cameraDialogStore;

	return (
		<GridRow gap={3} marginBottom={1}>
			{
				Object.keys(CUBE_FACE_DATA).map((face, index) => {
					const faceData = CUBE_FACE_DATA[face];
					const { res, cameraData, color } = faceData;
					return (
						<GridRow gap={1} key={index}>
							<IconButton
								onClick= {() => modelViewerSetCameraData(cameraData)}
								style={{
									width: "100%",
									padding: "0px 4px",
								}}
							>
								<Box
									style={{
										width: "18px",
										height: "18px",
										marginRight: "5px",
										borderRadius: "3px",
										border: "1px solid rgba(0, 0, 0, 0.10)",
										backgroundColor: color,
									}}
								/>
								<Text
									bold
									res={res}
									size={10}
								/>
							</IconButton>
						</GridRow>
					);
				})
			}
		</GridRow>
	);
});

const CameraPositionDialogContent: FunctionComponent = observer(() => {
	const cameraDialogStore = useCameraPositionDialogStore();
	const { setCameraData, initialCameraData } = cameraDialogStore;
	const [store, error] = useModelViewerLoader(null, {}, (store) => {
		const { toggleAxisHelper, toggleGridVisible, setOnCameraDataUpdate, getCanvasBlob, setCameraData: mvSetCameraData } = store;
		toggleGridVisible();
		toggleAxisHelper();
		store.enablePan = false;
		setCameraData(initialCameraData || store.canvasCameraData);
		mvSetCameraData(initialCameraData);
		setOnCameraDataUpdate((data: CameraData) => {
			setCameraData(data);
		});

		runInAction(() => {
			cameraDialogStore.modelViewerGetCanvasBlob = getCanvasBlob;
			cameraDialogStore.modelViewerSetCameraData =  mvSetCameraData;
		});
	});

	if (!store){
		return null;
	}

	return (
		<GridColumn>
			 <GridRow
				alignItems="flex-start"
				growChild={1}
				spacing={8}
			 >
				<Box height="300px" minWidth={400}>
					<ModelViewer store={store} />
				</Box>
				<GridColumn height="300px" justifyContent="space-between">
					<CameraPositionFormData />
					<CubeFaceMenu />
				</GridColumn>
			</GridRow>
		</GridColumn>
	);
});

const SaveAndCancelButtons: FunctionComponent = observer(() => {
	const { onCancel, saveCameraData, userCanSaveNewCameraPosition, setIsSaving }  = useCameraPositionDialogStore();

	const handleSaveClick = async () => {
		setIsSaving(true);
		await saveCameraData();
	};

	return (
		<GridRow justifyContent="flex-end" spacing={2}>
			<Button
				design="primary"
				disabled={userCanSaveNewCameraPosition}
				onClick={handleSaveClick}
				res={ButtonEnum.BUTTON_SAVE}
			/>
			<Button
				design="link"
				fullWidth
				onClick={onCancel}
				res={ButtonEnum.BUTTON_CANCEL}
			/>
		 </GridRow>
	);
});

const NewCameraPositionDialog: FunctionComponent<CameraPositionDialogProps> = observer((props) => {
	const { store } = props;

	return (
		<CameraPositionDialogStoreContext.Provider value={store}>
			<ModalDialog
				maxWidth="md"
				onClose={store.onCancel}
				open
				showClose
				showDividers
				titleRes="3DNewCameraPosition"
				width="100%"
			>
				{{
					buttons: (
						<SaveAndCancelButtons />
					),
					main: (
						<CameraPositionDialogContent />
					),
				}}
			</ModalDialog>
		</CameraPositionDialogStoreContext.Provider>
	);
});

export default NewCameraPositionDialog;